<script lang="ts">
  import Arrow from "@shared/svelte/Components/Carousel/Arrow.svelte";
  import CarouselTitle from "@shared/svelte/Components/Carousel/CarouselTitle.svelte";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import ProductCard from "@shared/svelte/Components/ProductLines/ProductCard.svelte";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";
  import { CarouselEntity } from "@shared/services/event_tracking/entities";
  import { debounce } from "lodash";

  import { getPricesForSearchResult } from "@retailer/services/pricing_data_service";

  import type { ProductLine, InventoryPrice } from "@shared/types/ProductLine";

  const { trackClick, trackEvent } = EventTracking;

  export let carouselIdentifier: string;
  export let carouselName: string;
  export let forwardLink: string = "";
  export let maxCards: number = 10;
  export let carouselCardsShown: number = 5;
  export let priceSegmentationBuckets: number[] | null = null;
  export let productLinePriceBucket: number | null = null;
  export let productLines: ProductLine[];
  export let outsideContainer: boolean = false;
  export let shortenNameOnMobile: boolean = true;
  export let sponsoredFlag: boolean = false;
  export let showTitle: boolean = true;
  export let shoppableUnit: boolean = false;

  // DO NOT USE: This is solely for testing purposes because we cannot mock the clientWidth,
  // which needs to be > 700 to be able to test the left arrow button.
  export let forceShowLeftArrow: boolean = false;

  const finalProductLines = maxCards ? productLines.slice(0, maxCards) : productLines;

  let carouselTrackingParams: CarouselEntity = new CarouselEntity({
    name: carouselIdentifier,
    productLinePriceBucket,
    priceSegmentationBuckets,
  });

  let loadingPrices = true;
  let inventoryPrices: InventoryPrice[] = [];
  if (productLines.length) {
    getPricesForSearchResult(productLines)
      .then(res => {
        loadingPrices = false;
        inventoryPrices = res;
      })
      .catch(() => {
        loadingPrices = false;
      });
  }

  let carouselContainer: HTMLElement;
  let carouselWidth: number = 0;

  // used for tracking to check for items out of view
  function checkForItemsHidden(element) {
    if (!element || !carouselContainer) {
      return true;
    }

    // is the product card left greater than the carousel container right?
    return element.getBoundingClientRect().left > carouselContainer.getBoundingClientRect().right;
  }

  let scrollingDueToClick = false;
  let trackedScrollLeft = 0;
  const trackScrolling = () => {
    if (!carouselContainer) {
      return;
    }

    if (!scrollingDueToClick) {
      if (trackedScrollLeft > carouselContainer.scrollLeft) {
        trackEvent("scroll", carouselTrackingParams, "carousel_left_scroll");
      } else if (trackedScrollLeft < carouselContainer.scrollLeft) {
        trackEvent("scroll", carouselTrackingParams, "carousel_right_scroll");
      }
    }
    scrollingDueToClick = false;
    trackedScrollLeft = carouselContainer.scrollLeft;
  };
  const debouncedTrackScrolling = debounce(trackScrolling, 200);

  function setScrollPosition() {
    debouncedTrackScrolling();

    carouselScrollLeft = carouselContainer.scrollLeft > 0;
    const maxScrollLeft = carouselContainer.scrollWidth - carouselContainer.clientWidth - 16; // padding
    carouselScrollRight = carouselContainer.scrollLeft < maxScrollLeft;
  }

  let startX: number, endX: number;
  let grabbed = false;
  // this number controls how far you need to move to scroll the carousel
  const scrollThreshold = 20;

  const handleSwipeStart = e => {
    grabbed = true;
    carouselContainer.style.setProperty("cursor", "grabbing");
    startX = e.clientX;
  };

  const handleSwipeEnd = e => {
    grabbed = false;
    carouselContainer.style.setProperty("cursor", "grab");

    endX = e.clientX;

    const deltaX = endX - startX;

    if (deltaX > scrollThreshold) {
      // swipe right
      carouselContainer.scrollBy({
        left: -carouselWidth,
        behavior: "smooth",
      });
    } else if (deltaX < -scrollThreshold) {
      // swipe left
      carouselContainer.scrollBy({
        left: carouselWidth,
        behavior: "smooth",
      });
    }
  };

  function handlePointer(e) {
    if (e.relatedTarget !== carouselContainer) {
      grabbed = false;
      carouselContainer.style.setProperty("cursor", "grab");
    }
  }

  let gap = 16;

  function scroll(direction: any) {
    if (!carouselContainer) {
      return;
    }
    let width = carouselWidth * direction;

    if (shoppableUnit) {
      if (direction === 1) {
        width = carouselWidth;
      } else {
        width = carouselWidth + 1;
        width = parseInt(`-${width}`);
      }
    }

    if (direction === 1) {
      width += gap;
    } else {
      width -= gap;
    }
    carouselContainer.scrollBy({
      left: width,
      behavior: "smooth",
    });
  }

  // reactive variables
  $: shoppableCardWidth = 0;

  // Calculate whether to show the left and right arrows
  $: carouselScrollLeft = false;
  $: carouselScrollRight = true;

  $: windowWidth = 0;

  $: productCardWidth = 0;
  $: showRightArrow = false;

  $: {
    if (shoppableUnit) {
      let totalWidth = productCardWidth * finalProductLines.length - 1; // product card width * finalProductLines.length - 1 for the shoppable unit
      let totalGap = gap * finalProductLines.length - 1; // gap is 16, 16 * finalProductLines.length - 1 for the shoppable unit
      let totalBorders = finalProductLines.length * 2 + 2; // shoppable unit has 2 borders, product card has 2 borders each
      // if the carousel width is less than the total width of the product cards + shoppable unit + gaps + borders the card is hidden, otherwise it is not

      showRightArrow =
        carouselWidth < totalWidth + shoppableCardWidth + totalGap + totalBorders &&
        carouselScrollRight;
    } else {
      showRightArrow = carouselScrollRight && finalProductLines.length > carouselCardsShown;
    }
  }
</script>

<style>
  /* These styles need to be left in here to be shared with SCH pages, please do not remove to a separate file */
  .carousel-outside-container {
    max-width: 100%;
    width: 100%;
    margin-inline: auto;
  }

  .carousel-outside-padding {
    padding-inline: 1rem;
  }

  .carousel-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .carousel-items {
    display: flex;
    gap: 1rem;
    align-items: stretch;
    justify-content: flex-start;
    overflow-x: scroll;
  }

  .carousel-button-container {
    position: relative;
  }

  .carousel-item {
    scroll-snap-align: center;
  }

  .product-carousel-item {
    min-width: 166px;
    max-width: 375px;
  }

  .carousel-shoppable-unit {
    max-width: 502px;
  }

  @media (min-width: 700px) {
    .carousel-shoppable-unit {
      min-width: 335px;
    }
    .product-carousel-item {
      max-width: 250px;
    }

    .carousel-items {
      overflow-x: hidden;
    }
  }

  @media (min-width: 1024px) {
    .product-carousel-item {
      min-width: 127px;
    }

    .carousel-outside-padding {
      padding-inline: 2rem;
    }

    .carousel-shoppable-unit + .product-carousel-item {
      max-width: 250px;
      flex: 1 0 20%;
    }

    .carousel-shoppable-unit {
      max-height: 100%;
      flex: 1 0 min-content;
    }

    .flex-width {
      flex: 1 0 auto;
    }

    .full-height {
      height: 100%;
    }
  }

  @media (min-width: 1440px) {
    .carousel-outside-container {
      flex: 1 0 auto;
      max-width: 1440px;
    }
    .carousel-shoppable-unit {
      min-width: 502px;
    }
  }
</style>

<svelte:window bind:innerWidth={windowWidth} />

<TrackingContext entities={carouselTrackingParams} let:trackingContext>
  <section
    class="carousel-container"
    aria-labelledby={"carousel"}
    class:carousel-outside-padding={outsideContainer}
    class:carousel-outside-container={outsideContainer}
    data-testid="{carouselIdentifier}-carousel"
  >
    {#if showTitle}
      <CarouselTitle {carouselName} {forwardLink} {sponsoredFlag} {shortenNameOnMobile} />
    {/if}
    <div class="carousel-button-container">
      {#if carouselScrollLeft || forceShowLeftArrow}
        <Arrow
          name="prev"
          data-testid="carousel-nav-button-left"
          on:click={() => {
            scrollingDueToClick = true;
            trackClick("carousel_left_arrow", trackingContext.entities);
            scroll(-1);
          }}
        />
      {/if}

      {#if showRightArrow}
        <Arrow
          name="next"
          data-testid="carousel-nav-button-right"
          on:click={() => {
            scrollingDueToClick = true;
            trackClick("carousel_right_arrow", trackingContext.entities);
            scroll(1);
          }}
        />
      {/if}
      <ul
        id={carouselIdentifier}
        class="carousel-items"
        data-testid="carousel-items-container"
        bind:clientWidth={carouselWidth}
        bind:this={carouselContainer}
        on:scroll={setScrollPosition}
        on:pointerdown|preventDefault={handleSwipeStart}
        on:pointerup={handleSwipeEnd}
        on:pointerleave={handlePointer}
      >
        {#if shoppableUnit}
          <li
            class="carousel-item carousel-shoppable-unit"
            class:full-height={carouselWidth >= 975}
            class:flex-width={carouselWidth >= 975}
            bind:clientWidth={shoppableCardWidth}
          >
            <slot name="shoppable-unit" />
          </li>
        {/if}
        {#each finalProductLines as productLine, index}
          <li
            class:shoppable-product={shoppableUnit}
            class="product-carousel-item carousel-item"
            style={finalProductLines.length > 3 && windowWidth >= 700
              ? `max-width: calc(100% / ${carouselCardsShown} - ${
                  carouselCardsShown === 5 ? 13 : 12
                }px);
        flex: 1 0 calc(100% /  ${carouselCardsShown} - ${carouselCardsShown === 5 ? 13 : 12}px);`
              : windowWidth >= 700
              ? `flex: 1 0 auto;`
              : ``}
            bind:clientWidth={productCardWidth}
          >
            <ProductCard
              extraDistributorTooltipClass={"fizz-tooltip-top product-card-carousel"}
              hideSalesDivisions={true}
              {index}
              {productLine}
              {grabbed}
              {inventoryPrices}
              bind:loadingPrices
              checkIfHiddenByCarousel={checkForItemsHidden}
              showFuturePriceAvailability={false}
            />
          </li>
        {/each}
      </ul>
    </div>
  </section>
</TrackingContext>
